import React, { useMemo } from 'react';

import { defineMessage, useIntl } from 'react-intl';

import { createQuickInsertMatcher } from '@atlaskit/editor-common/quick-insert/create-quick-insert-matcher';
import { ASK_ROVO_MENU_ITEM, ROVO_SECTION } from '@atlaskit/editor-common/quick-insert/keys';
import { messages } from '@atlaskit/editor-common/quick-insert/messages';
import type { QuickInsertPreview } from '@atlaskit/editor-common/quick-insert/preview';
import { ROVO_SECTION_RANK } from '@atlaskit/editor-common/quick-insert/rank';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import type { RegisterMenuItem } from '@atlaskit/editor-ui-control-model/types';

import type { AIPlugin } from '../../aiPluginType';
import type { AIGlobalOptIn, EditorPluginAIProvider } from '../../types/types';
import {
	canShowEditorPluginAIConfigItem,
	getTranslatedItemTitle,
	isEditorPluginAIConfigItemWithOptions,
} from '../prebuilt/config-items/config-items';
import { getIsAiEntryPointDisabled } from '../toolbars/utils/use-is-ai-entry-point-disabled';
import { AIQuickInsertMenuItem } from './AIQuickInsertMenuItem';

const askRovoDescription = defineMessage({
	id: 'editor-plugin-ai.quick-insert.ask-rovo-description.ai-non-final',
	defaultMessage: 'Create and improve content with AI',
	description:
		'Description in the Ask Rovo slash-command preview, explaining that Rovo helps the user work with AI.',
});

const askRovoPreview: QuickInsertPreview = {
	image: {
		light: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVKU.png',
		dark: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVI4.png',
	},
};

type Params = {
	aiGlobalOptIn: AIGlobalOptIn;
	api: ExtractInjectionAPI<AIPlugin> | undefined;
	editorPluginAIProvider: EditorPluginAIProvider;
	getEditorView: () => EditorView | undefined;
	isRovoAvailable: boolean;
};

export const getAIQuickInsertComponents = ({
	aiGlobalOptIn,
	api,
	editorPluginAIProvider,
	getEditorView,
	isRovoAvailable,
}: Params): RegisterMenuItem[] => {
	const components: RegisterMenuItem[] = [];

	if (isRovoAvailable) {
		components.push({
			key: ASK_ROVO_MENU_ITEM.key,
			type: ASK_ROVO_MENU_ITEM.type,
			parents: [
				{
					key: ROVO_SECTION.key,
					type: ROVO_SECTION.type,
					rank: ROVO_SECTION_RANK[ASK_ROVO_MENU_ITEM.key],
				},
			],
			match: createQuickInsertMatcher(({ formatMessage }) => {
				const title = getTranslatedItemTitle(editorPluginAIProvider.baseGenerate, formatMessage);
				return {
					description: formatMessage(askRovoDescription),
					keywords: ['ai', 'generate', 'rovo', title],
					title,
				};
			}),
			component: function AskRovoQuickInsertMenuItem() {
				const { formatMessage } = useIntl();
				const preview = useMemo(
					() => ({
						...askRovoPreview,
						attribution: { name: formatMessage(messages.previewAttributionAtlassian) },
					}),
					[formatMessage],
				);
				return (
					<AIQuickInsertMenuItem
						aiGlobalOptIn={aiGlobalOptIn}
						api={api}
						configItem={editorPluginAIProvider.baseGenerate}
						description={formatMessage(askRovoDescription)}
						preview={preview}
					/>
				);
			},
			isHidden: () => getIsAiEntryPointDisabled(api),
		});
	}

	if (editorPluginAIProvider.isInDocumentStreamingEnabled) {
		return components;
	}

	(editorPluginAIProvider.configItemWithOptions ?? [])
		.filter(isEditorPluginAIConfigItemWithOptions)
		.forEach((item) => {
			components.push({
				key: `quick-insert-ai-${item.config.key}`,
				type: 'menu-item',
				parents: [
					{
						key: ROVO_SECTION.key,
						type: ROVO_SECTION.type,
						rank: item.config.quickInsertPriority ?? 9000,
					},
				],
				match: createQuickInsertMatcher(({ formatMessage }) => {
					const title = getTranslatedItemTitle(item.config, formatMessage);
					return {
						description: formatMessage(item.config.description ?? item.config.title),
						keywords: ['ai', 'generate', 'rovo', title],
						title,
					};
				}),
				component: () => (
					<AIQuickInsertMenuItem aiGlobalOptIn={aiGlobalOptIn} api={api} configItem={item.config} />
				),
				isHidden: () => {
					const editorView = getEditorView();
					return (
						getIsAiEntryPointDisabled(api) ||
						!editorView ||
						!canShowEditorPluginAIConfigItem({
							editorView,
							positions: [0, 0],
							isEmpty: true,
							item,
						})
					);
				},
			});
		});

	return components;
};
